<template>
  <div :class="['pf-v6-c-switchlist', $attrs.class]" :style="$attrs.style" v-bind="ouiaProps">
    <input v-if="name && !value.length" type="hidden" :name="name" value="" />

    <slot>
      <pf-switch
        v-for="o of normalizedOptions"
        :key="$as<string>(o.value)"
        v-bind="{ ...$attrs, style: undefined, class: undefined }"
        :checked="value.includes(o.value)"
        :name="name ? `${name}[${o.value}]` : undefined"
        :value="o.value"
        :label="o.label"
        :disabled="disabled"
        @update:checked="$event ? addItem(o) : removeItem(o)"
        :ouia-id="`${ouiaProps['data-ouia-component-id']}-${o.value}`"
      />
    </slot>
  </div>
</template>

<style lang="scss">
:root {
  --pf-v6-c-switchlist--ColumnWidth: 250px;
}

.pf-v6-c-switchlist {
  column-width: var(--pf-v6-c-switchlist--ColumnWidth);

  .pf-v6-c-switch {
    margin: 1px 0;
    min-width: var(--pf-v6-c-switchlist--ColumnWidth);
  }
}
</style>

<script lang="ts">
// Tipo helper per validare che labelField sia una chiave valida
export type ValidateLabelField<O, L extends PropertyKey> = O extends readonly (infer U)[]
  ? [L] extends [never]
    ? L // Se L è never, è valido (array di primitivi)
    : U extends Record<L, string>
      ? L // Se U ha la proprietà L di tipo string
      : never
  : L;

// Tipo helper per validare che valueField sia una chiave valida
export type ValidateValueField<O, V extends PropertyKey> = O extends readonly (infer U)[]
  ? [V] extends [never]
    ? V // Se V è never, è valido (array di primitivi)
    : U extends Record<V, any>
      ? V // Se U ha la proprietà V
      : never
  : V;

export interface PfSwitchListProps<O, L extends PropertyKey = never, V extends PropertyKey = never, T = ExtractValueType<O, L, V>>
  extends /* @vue-ignore */ HTMLAttributes {
  name?: string;
  modelValue?: Partial<T>[];
  items?: O;
  labelField?: ValidateLabelField<O, L> | ((option: ExtractOptionType<O>) => string);
  valueField?: ValidateValueField<O, V>;
  disabled?: boolean;
}
</script>

<script setup lang="ts" generic="O, L extends PropertyKey = never, V extends PropertyKey = never">
import { type HTMLAttributes, computed } from 'vue';
import { useOUIAProps, type OUIAProps } from '@common/ouia';
import type { ExtractOptionType, ExtractValueType } from './typeutils';
import { cast as $as } from './utils';

defineOptions({
  inheritAttrs: false,
});

type T = ExtractValueType<O, L, V>;

type NormalizedOption = {
  option: ExtractOptionType<O>;
  label: string;
  value: T;
};

const props = withDefaults(defineProps<OUIAProps & PfSwitchListProps<O, L, V, T>>(), {
  items: () => [] as any,
});
const ouiaProps = useOUIAProps(props);

const value = defineModel<T[]>('modelValue', { default: () => [] });

const emit = defineEmits<{
  (name: 'update:modelValue', value: T[]): void;
}>();

function removeItem(item: NormalizedOption) {
  const i = value.value.indexOf(item.value);
  if (i < 0) {
    return;
  }

  const items = [...value.value];
  items.splice(i, 1);
  value.value = items;
}

function addItem(item: NormalizedOption) {
  if (!value.value.includes(item.value)) {
    value.value = [...value.value, item.value];
  }
}

function label(option: ExtractOptionType<O>): string {
  if (typeof props.labelField === 'function') {
    return props.labelField(option as any);
  }
  return String(typeof option === 'object' ? (option as Record<string, any>)[props.labelField as any] : option);
}

const normalizedOptions = computed(() => {
  const norm: NormalizedOption[] = [];
  if (Array.isArray(props.items)) {
    for (const option of props.items) {
      const isObj = typeof option === 'object';
      const value = isObj ? option[props.valueField as keyof typeof option] : option;
      const labelString = label(option);
      norm.push({
        option,
        label: labelString,
        value,
      });
    }
  } else {
    for (const [value, option] of Object.entries(props.items as Record<string, any>)) {
      const labelString = label(option);
      norm.push({
        option,
        label: labelString,
        value: value as T,
      });
    }
  }
  return norm;
});
</script>
